import { addressEqual } from 'nftx.js';
import { SwapState } from './types';

const identity = (state: SwapState) => state;

const ethSellEthBuy = (state: SwapState): SwapState => {
  // You cannot trade eth for eth
  if (state.filterController === 'buy') {
    return {
      ...state,
      sellType: 'NFT',
    };
  }

  return {
    ...state,
    buyType: 'NFT',
  };
};
const ethSellVTokenBuy = identity;
const ethSellNftBuy = identity;

const vTokenSellEthBuy = identity;
const vTokenSellVTokenBuy = (state: SwapState): SwapState => {
  const { sellToken, buyToken, filterController } = state;

  if (!sellToken || !buyToken) {
    return state;
  }
  if (sellToken === buyToken) {
    // Cannot trade vToken for itself
    if (filterController === 'buy') {
      return { ...state, sellToken: '', sellVaultAddress: '' };
    }
    return { ...state, buyVaultId: '', buyToken: '', buyVaultAddress: '' };
  }
  return state;
};
const vTokenSellNftBuy = (state: SwapState): SwapState => {
  const { buyItems, buyVaultAddress, sellVaultAddress, filterController } =
    state;

  // We've picked NFTs to buy but not yet picked a vToken
  // Default to ETH
  if (buyItems.length && !sellVaultAddress) {
    return {
      ...state,
      sellAmount: '',
      sellToken: '',
      sellItems: [],
      sellType: 'ETH',
      sellVaultAddress: '',
      sellVaultId: '',
    };
  }

  if (!sellVaultAddress || !buyItems.length) {
    return state;
  }

  if (addressEqual(buyVaultAddress, sellVaultAddress)) {
    // All NFTs we're buying are from a single vault
    // And that vault matches the vToken we're selling
    return state;
  }

  // Cannot swap vToken for NFTs from another vault
  if (filterController === 'buy') {
    return {
      ...state,
      sellToken: '',
      sellAmount: '',
    };
  }
  return {
    ...state,
    buyToken: '',
    buyVaultId: '',
    buyItems: [],
  };
};

const nftSellEthBuy = identity;
const nftSellVTokenBuy = (state: SwapState): SwapState => {
  const { sellVaultAddress, buyVaultAddress, filterController, sellItems } =
    state;

  // We've picked NFTs to sell but not yet picked a vToken
  // Default to ETH
  if (sellItems.length && !buyVaultAddress) {
    return {
      ...state,
      buyAmount: '',
      buyItems: [],
      buyToken: '',
      buyType: 'ETH',
      buyVaultId: '',
      buyVaultAddress: '',
    };
  }

  if (!sellVaultAddress || !buyVaultAddress) {
    return state;
  }
  if (addressEqual(sellVaultAddress, buyVaultAddress)) {
    // Selling an NFT for the same vault as the vToken
    return state;
  }
  // Cannot swap NFTs for vToken from another vault
  if (filterController === 'buy') {
    return {
      ...state,
      sellToken: '',
      sellVaultAddress: '',
      sellVaultId: '',
      sellItems: [],
    };
  }
  return {
    ...state,
    buyToken: '',
    buyVaultId: '',
    buyAmount: '',
    buyVaultAddress: '',
    buyItems: [],
  };
};
const nftSellNftBuy = (state: SwapState): SwapState => {
  const { buyVaultId, sellVaultId, filterController } = state;

  if (!buyVaultId && !sellVaultId) {
    return state;
  }
  if (!buyVaultId) {
    return {
      ...state,
      buyAmount: '',
      buyItems: [],
      buyToken: '',
      buyVaultId: '',
      buyType: 'ETH',
      buyVaultAddress: '',
    };
  }
  if (!sellVaultId) {
    return {
      ...state,
      sellAmount: '',
      sellToken: '',
      sellItems: [],
      sellType: 'ETH',
      sellVaultAddress: '',
      sellVaultId: '',
    };
  }
  if (buyVaultId === sellVaultId) {
    // Every NFT we're buying is from the same vault as the NFTs we're selling
    return state;
  }

  // Cannot swap NFTs across vaults
  if (filterController === 'buy') {
    return {
      ...state,
      sellToken: '',
      sellItems: [],
      sellVaultAddress: '',
      sellVaultId: '',
      sellAmount: '',
      sellType: 'ETH',
    };
  }

  return {
    ...state,
    buyToken: '',
    buyVaultId: '',
    buyItems: [],
    buyAmount: '',
    buyType: 'ETH',
    buyVaultAddress: '',
  };
};

/** Takes a state object and ensures all of the properties are valid.
 * If there's an invalid value it will return a new state object with valid values.
 */
const sanitizeState = (state: SwapState): SwapState => {
  const { sellType, buyType } = state;

  switch (sellType) {
    case 'ETH':
      switch (buyType) {
        case 'ETH':
          return ethSellEthBuy(state);
        case 'vTOKEN':
          return ethSellVTokenBuy(state);
        case 'NFT':
          return ethSellNftBuy(state);
        default:
          break;
      }
    case 'vTOKEN':
      switch (buyType) {
        case 'ETH':
          return vTokenSellEthBuy(state);
        case 'vTOKEN':
          return vTokenSellVTokenBuy(state);
        case 'NFT':
          return vTokenSellNftBuy(state);
        default:
          break;
      }
    case 'NFT':
      switch (buyType) {
        case 'ETH':
          return nftSellEthBuy(state);
        case 'vTOKEN':
          return nftSellVTokenBuy(state);
        case 'NFT':
          return nftSellNftBuy(state);
        default:
          break;
      }
    default:
      break;
  }

  return state;
};

export default sanitizeState;
